import { useTranslation } from 'react-i18next'
import {
  type MATERIAL_TYPES,
  type TILE_SIZES,
} from '@mixtiles/web-backend-shared'
import { calculateDiscountPercent, formatPrice } from '../utils/utils'
import { WELCOME_COUPON } from '../stores/promoCodeStore/promoCode.consts'
import { usePricing } from '../components/Pricing/PricingProvider'
import { useProductType } from '../services/ProductTypesManager'
import { PRODUCT_TYPES } from '../services/ProductTypeState'
import { DiscountType } from '../components/Pricing/types/discount.types'

// Derived-only: holding these values in state via effects keyed on the
// fresh-per-call fetch results causes infinite update loops
export default function useBundleDiscount({
  tileSize,
  materialType,
}: {
  tileSize: TILE_SIZES
  materialType: MATERIAL_TYPES
}) {
  const { t } = useTranslation()
  const { productType } = useProductType()
  const isBundleSupported = ![
    PRODUCT_TYPES.PHOTO_BOOK,
    PRODUCT_TYPES.BUSINESS,
  ].includes(productType)
  const { fetchDiscount, fetchPricingItem, pricingData } = usePricing()

  const discount =
    isBundleSupported && !pricingData.isLoading
      ? fetchDiscount({ tileSize, materialType })
      : null

  const pricingItem = discount
    ? fetchPricingItem({ tileSize, materialType }, 'useBundleDiscount')
    : null

  const isLoading =
    pricingData.isLoading ||
    !discount ||
    !pricingItem ||
    discount.isLoading ||
    pricingItem.isLoading

  const bundleMetadata =
    !isLoading && discount.value?.type === DiscountType.BUNDLE
      ? discount.value.metadata
      : null
  const singleTilePrice = pricingItem?.value ?? null

  const bundleDescription = bundleMetadata
    ? t('general.promo_code_success_dialog.title.bundle', {
        v1: bundleMetadata.bundleTiles,
        v2: formatPrice(
          bundleMetadata.bundlePrice,
          bundleMetadata.bundleCurrency
        ),
      })
    : null

  const subtitle =
    bundleMetadata && singleTilePrice
      ? t('general.menu.use_code_to_save', {
          v1: WELCOME_COUPON,
          v2: `${calculateDiscountPercent(
            bundleMetadata.bundlePrice,
            singleTilePrice * bundleMetadata.bundleTiles
          )}`,
        })
      : null

  const originalBundlePrice =
    bundleMetadata && singleTilePrice
      ? singleTilePrice * bundleMetadata.bundleTiles
      : null

  return {
    isLoading: isBundleSupported ? isLoading : false,
    discount,
    bundleDescription,
    originalBundlePrice,
    subtitle,
    pricingData,
  }
}
